import { SalesDocumentType, flattenConnections, SalesDocument, SalesDocumentConnection, SalesDocumentPosition, State, DocumentPosition, IccDocumentPosition, ComplaintPosition, Complaint, ComplaintAppointment, ComplaintsDocumentType, ComplaintSparePart, ComplaintNote } from '@icc/b2b/shared';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DocumentAttachment } from 'libs/b2b/shared/src/lib/models/document-attachment.model';
import { SALES_DOCUMENTS_FEATURE_KEY, SalesDocumentsPartialState } from './sales-documents.reducer';
import { logger } from '@icc/shared';

// Lookup the 'SalesDocuments' feature state managed by NgRx
export const getSalesDocumentsState = createFeatureSelector<State>(SALES_DOCUMENTS_FEATURE_KEY);

export const getSalesDocumentsLoaded = createSelector(
    getSalesDocumentsState,
    (state: State) => state.loaded
);

export const getSalesDocumentsError = createSelector(
    getSalesDocumentsState,
    (state: State) => state.error
);

export const getAllSalesDocuments = createSelector(
    getSalesDocumentsState,
    (state: State) => state.documentsIds.map(el => state.documents[el]).filter((e) => !e.isDeleted)
);

export const getSalesDocumentsEntities = createSelector(
    getSalesDocumentsState,
    (state: State) => state.documents
);

export const getAllChanges = createSelector(getSalesDocumentsState, (state: State) => ({
    documents: Object.values(state.documents),
    documentsIds: state.documentsIds,
    positions: Object.values(state.positions),
    appointments: Object.values(state.appointments),
    complaintNotes: Object.values(state.complaintNotes),
    complaintSpareParts: Object.values(state.complaintSpareParts),
    attachments: state.attachments,
    operations: state.operations,
    deliveries: Object.values(state.deliveries),
    documentConnections: state.documentConnections,
    opsToSendToWorker: state.opsToSendToWorker,
    opsToSend: state.opsToSend,
    opsToSave: state.opsToSave,
    changesOnServer: state.changesOnServer,
    synchronizationInProgress: state.synchronizationInProgress,
    staleDocs: state.staleDocs,
    graphsToSend: state.graphsToSend,
}));


export const getOperationsToSend = createSelector(
    getSalesDocumentsState,
    (state: State) => state.opsToSend
);

export const getOperationsToSave = createSelector(
    getSalesDocumentsState,
    (state: State) => state.opsToSave
);

export const getChangesToSave = createSelector(
    getSalesDocumentsState,
    (state: State) => ({
        opsToSave: state.opsToSave,
        graphsToSend: state.graphsToSend,
        opsToSendToWorker: state.opsToSendToWorker,
    })
);

export const getAllOperations = createSelector(
    getSalesDocumentsState,
    (state: State) => state.operations
);

export const getDocsEntities = createSelector(getSalesDocumentsState, (state: State) => state.documents);

export const getAllPositions = createSelector(
    getSalesDocumentsState,
    (state: State) => Object.values(state.positions)
);

export const getAllAppointments = createSelector(
    getSalesDocumentsState,
    (state: State) => Object.values(state.appointments)
);

export const getAllComplaintNotes = createSelector(
    getSalesDocumentsState,
    (state: State) => Object.values(state.complaintNotes)
);

export const getAllComplaintSpareParts = createSelector(
    getSalesDocumentsState,
    (state: State) => Object.values(state.complaintSpareParts)
);

export const getAllAttachments = createSelector(
    getSalesDocumentsState,
    (state: State) => state.attachments.filter(e => e)
);

export const getSelectedId = createSelector(
    getSalesDocumentsState,
    (state: State) => state.selectedId
);

export const getSelected = createSelector(
    getSalesDocumentsEntities,
    getSelectedId,
    (entities, selectedId) => entities[selectedId]
);

export const getSelectedSales = createSelector(
    getSalesDocumentsEntities,
    getSelectedId,
    (entities, selectedId) => {
        const selected = entities[selectedId];
        if (selected && selected.type === 'sales') {
            return selected;
        }
    }
);

export const getSelectedComplaint = createSelector(
    getSalesDocumentsEntities,
    getSelectedId,
    (entities, selectedId) => {
        const selected = entities[selectedId];
        if (selected && selected.type === 'complaint') {
            return selected;
        }
    }
);

export const getSubdocuments = (rootId: string, type: SalesDocumentType | ComplaintsDocumentType) => createSelector(
    getAllSalesDocuments,
    (entities) => entities.filter((e) =>
        rootId.startsWith('c-') ? e.type === 'complaint' && e.rootId === rootId && e.documentTypeId === type :
        e.type === 'sales' && e.rootId === rootId && e.salesDocumentTypeId === type
    ) as SalesDocument[]
);

export const getDocPositions = createSelector(
    getAllPositions,
    getSelectedId,
    (entities, selectedId) =>
        entities.filter((e) => e.documentId === selectedId)
);

export const getDocAppointments = createSelector(
    getAllAppointments,
    getSelectedId,
    (entities, selectedId) =>
        entities.filter((e) => e.documentId === selectedId && !e.isDeleted)
);

export const getDocComplaintNotes = createSelector(
    getAllComplaintNotes,
    getSelectedId,
    (entities, selectedId) =>
        entities.filter((e) => e.documentId === selectedId)
);

export const getDocComplaintSpareParts = createSelector(
    getAllComplaintSpareParts,
    getSelectedId,
    (entities, selectedId) =>
        entities.filter((e) => e.documentId === selectedId && !e.isDeleted)
);

export const getSelectedSalesDocumentPositions = createSelector(
    getAllPositions,
    getSelectedId,
    (entities, selectedId) =>
        entities.filter((e) => {
            if (e.type !== 'defect') {
                return e.documentId === selectedId;
            }
            return false;
        }) as SalesDocumentPosition[]
);

export const getSelectedComplaintPositions = createSelector(
    getAllPositions,
    getSelectedId,
    (entities, selectedId) =>
        (entities.filter((e) => {
            if (e.type === 'defect') {
                return e.documentId === selectedId && !e.isDeleted;
            }
            return false;
        }) as ComplaintPosition[]).sort((a, b) => a?.index - b?.index)
);

export const getSelectedComplaintAppointments = createSelector(
    getAllAppointments,
    getSelectedId,
    (entities, selectedId) =>
        entities.filter((e) => e.documentId === selectedId && !e.isDeleted) as ComplaintAppointment[]
);

export const getSelectedComplaintNotes = createSelector(
    getAllComplaintNotes,
    getSelectedId,
    (entities, selectedId) =>
        entities.filter((e) => e.documentId === selectedId) as ComplaintNote[]
);

export const getSelectedComplaintSpareParts = createSelector(
    getAllComplaintSpareParts,
    getSelectedId,
    (entities, selectedId) =>
        entities.filter((e) => e.documentId === selectedId) as ComplaintSparePart[]
);

export const getDocumentPositions = (documentId, all = false) =>
    createSelector(getAllPositions, (entities) =>
        entities.filter((e) => e.documentId === documentId && (all || !e.isDeleted)) as IccDocumentPosition[]
    );

export const getDocumentAppointments = (documentId) =>
    createSelector(getAllAppointments, (entities) =>
        entities.filter((e) => e.documentId === documentId && !e.isDeleted) as ComplaintAppointment[]
    );

export const getDocumentComplaintNotes = (documentId) =>
    createSelector(getAllComplaintNotes, (entities) =>
        entities.filter((e) => e.documentId === documentId) as ComplaintNote[]
    );

export const getDocumentComplaintSpareParts = (documentId) =>
    createSelector(getAllComplaintSpareParts, (entities) =>
        entities.filter((e) => e.documentId === documentId && !e.isDeleted) as ComplaintSparePart[]
    );

export const getSalesDocumentPositions = (documentId, all = false) =>
    createSelector(getAllPositions, (entities) =>
        entities.filter((e) => e.documentId === documentId && (all || !e.isDeleted) && e.type !== 'defect') as SalesDocumentPosition[]
    );

export const getDocAttachments = createSelector(
    getAllAttachments,
    getSelectedId,
    (entities, selectedId) =>
        entities.filter((e) => e.documentId === selectedId && !e.isDeleted && !e.documentPositionId)
);

export const getDocumentAttachments = (documentId) =>
    createSelector(getAllAttachments, (entities) =>
        entities.filter((e) => e.documentId === documentId && !e.isDeleted)
    );

export const getDocumentsAttachments = (documentsIds: string[]) =>
    createSelector(getAllAttachments, (entities) =>
        entities
        .filter((e) => documentsIds.includes(e.documentId) && !e.isDeleted)
        .reduce((prev, c) => {
            const positionAttachments = prev.get(c.documentPositionId ?? c.documentId) || [];
            positionAttachments.push(c);
            prev.set(c.documentPositionId ?? c.documentId, positionAttachments);
            return prev;
        }, new Map<string, DocumentAttachment[]>())
    );

export const getDocumentAppointmentAttachments = (documentsIds: string[]) =>
    createSelector(getAllAttachments, (entities) =>
        entities
        .filter((e) => documentsIds.includes(e.documentId) && !e.isDeleted)
        .reduce((prev, c) => {
            const appointmentAttachments = prev.get(c.appointmentId) || [];
            appointmentAttachments.push(c);
            prev.set(c.appointmentId, appointmentAttachments);
            return prev;
        }, new Map<string, DocumentAttachment[]>())
    );

export const getDocumentNoteAttachments = (documentsIds: string[]) =>
    createSelector(getAllAttachments, (entities) =>
        entities
        .filter((e) => documentsIds.includes(e.documentId) && !e.isDeleted)
        .reduce((prev, c) => {
            const noteAttachments = prev.get(c.complaintNoteId) || [];
            noteAttachments.push(c);
            prev.set(c.complaintNoteId, noteAttachments);
            return prev;
        }, new Map<string, DocumentAttachment[]>())
    );

export const getDocumentsConnections = createSelector(
    getSalesDocumentsState,
    (state: State) => state.documentConnections/* .filter((e) => !e?.buyAttachmentId) */
);

export const getDocumentsConnectionsForDocument = documentId => createSelector(
    getDocumentsConnections,
    (connections) => connections[documentId]
);

export const getAttachmentsConnections = createSelector(
    getSalesDocumentsState,
    (state: State) => flattenConnections(state.documentConnections).filter((e) => e?.buyAttachmentId && !e?.isDeleted)
);

export const getDocument = documentId => createSelector(
    getSalesDocumentsEntities,
    (entities) =>
        entities[documentId]
);

export const getSalesDocument = documentId => createSelector(
    getSalesDocumentsEntities,
    (entities) => {
        const doc = entities[documentId];
        if (doc && doc.type === 'sales') {
            return doc;
        }
    }
);

export const getComplaintsDocument = documentId => createSelector(
    getSalesDocumentsEntities,
    (entities) => {
        const doc = entities[documentId];
        if (doc && doc.type === 'complaint') {
            return doc as Complaint;
        }
    }
);

export const getSalesDocumentForBuyDocument = documentId => createSelector(
    getSalesDocumentsEntities,
    getDocumentsConnections,
    (entities, connections) => flattenConnections(connections).filter(el => el.buyDocumentId === documentId &&
        !el.buyPositionId && !el.buyAttachmentId && !el.isDeleted
    )
    .map((el) => entities[el.salesDocumentId])
    .filter(el => el && el.type === 'sales')
    .sort((a, b) => a.type === 'sales' && b.type === 'sales' && Number(a.isAnnexed) - Number(b.isAnnexed))[0] as SalesDocument
);

export const getBuyDocumentsForDocument = documentId => createSelector(
    getSalesDocumentsEntities,
    getDocumentsConnections,
    (entities, connections) => Object.keys(connections[documentId] ?? {}).map((el) => entities[el]).filter(c => c && c.type === 'sales') as SalesDocument[]
);

export const getBuyDocumentIdsForDocument = documentId => createSelector(
    getDocumentsConnections,
    (connections) => Object.keys(connections[documentId] ?? {})
);

export const getBuyDocumentsForDocuments = (documentIds: string[]) => createSelector(
    getSalesDocumentsEntities,
    getDocumentsConnections,
    (entities, connections) => documentIds.reduce((acu, documentId) => {
        acu.set(documentId, Object.keys(connections[documentId] ?? {}).map((el) => entities[el]).filter(c => c && c.type === 'sales') as SalesDocument[]);
        return acu;
    }, new Map<string, SalesDocument[]>())
);

export const getBuyDocumentAttachmentsByAttachmentUuid= attachmentId =>   createSelector(
    getAllAttachments,
    getAttachmentsConnections,
    (entities, connections) =>
        connections
            .filter((c) => c.salesAttachmentId === attachmentId)
            .map((c) => entities.find(e => e.uuid === c.buyAttachmentId))
);


export const getBuyDocumentsForSelectedDocument = createSelector(
    getSalesDocumentsEntities,
    getDocumentsConnections,
    getSelectedId,
    (entities, connections, selectedId) => {
        const buyDocumentsByParent = Object.keys(connections[selectedId] ?? [])
            .map((el) => entities[el])
            .filter(c => c && c.type === 'sales')
            .reduce<Record<string, SalesDocument[]>>((acc, el: SalesDocument) => {
                const firstId = el.rootId || el.uuid;
                if (!acc[firstId]) {
                    acc[firstId] = [];
                }
                acc[firstId].push(el);
                return acc;
            }, {});
        const buyDocuments: SalesDocument[] = [];
        for (const key in buyDocumentsByParent) {
            if (buyDocumentsByParent[key].length > 0) {
                buyDocuments.push(buyDocumentsByParent[key].sort((a, b) => Number(a.isAnnexed) - Number(b.isAnnexed))[0]);
            }
        }

        return buyDocuments as SalesDocument[];
    }
);

export const getBuyPositionsForPosition = positionId => createSelector(
    getDocumentsConnections,
    getAllPositions,
    (connections, positions) =>
        flattenConnections(connections)
            .filter((c) => c.salesPositionId === positionId && !c.isDeleted)
            .map(c => positions.find((e) => e.uuid === c.buyPositionId))
            .filter(c => c && c.type !== 'defect') as SalesDocumentPosition[]
);


export const getBuyPositionsForSelectedDocument = (all = false) => createSelector(
    getDocumentsConnections,
    getAllPositions,
    getSelectedId,
    (connections, positions, selectedId) => {
        const buyConnections: SalesDocumentConnection[] = [];
        if (connections[selectedId]) {
            for (const buyDocId in connections[selectedId]) {
                for (const uuid in connections[selectedId][buyDocId]) {
                    const c = connections[selectedId][buyDocId][uuid];
                    if (c.salesPositionId && (all || !c.isDeleted)) {
                        buyConnections.push(c);
                    }
                }
            }
        }

        if (all) {
            buyConnections.sort((a, b) => Number(a.isDeleted) - Number(b.isDeleted));
        }

        return buyConnections
            .reduce((prev, c) => {
                prev.set(c.salesPositionId, positions.find((e) => e.type !== 'defect' && e.uuid === c.buyPositionId) as SalesDocumentPosition);
                return prev;
            }, new Map<string, SalesDocumentPosition>());
    }
);


export const getBuyPositionsForDocument = (documentId: string, all = false) => createSelector(
    getDocumentsConnections,
    getAllPositions,
    (connections, positions) => {
        const buyConnections: SalesDocumentConnection[] = [];
        if (connections[documentId]) {
            for (const buyDocId in connections[documentId]) {
                for (const uuid in connections[documentId][buyDocId]) {
                    const c = connections[documentId][buyDocId][uuid];
                    if (c.salesPositionId && (all || !c.isDeleted)) {
                        buyConnections.push(c);
                    }
                }
            }
        }

        if (all) {
            buyConnections.sort((a, b) => Number(a.isDeleted) - Number(b.isDeleted));
        }

        return buyConnections
            .reduce((prev, c) => {
                prev.set(c.salesPositionId, positions.find((e) => e.type !== 'defect' && e.uuid === c.buyPositionId) as SalesDocumentPosition);
                return prev;
            }, new Map<string, SalesDocumentPosition>());
    }
);

export const getPositionAttachmentsForSelectedDocument = createSelector(
    getAllAttachments,
    getSelectedId,
    (attachments, selectedId) =>
        attachments
            .filter((c) => c.documentId === selectedId && c.documentPositionId && !c.isDeleted)
            .reduce((prev, c) => {
                const positionAttachments = prev.get(c.documentPositionId) || [];
                positionAttachments.push(c);
                prev.set(c.documentPositionId, positionAttachments);
                return prev;
            }, new Map<string, DocumentAttachment[]>())
);

export const getSynced = createSelector(
    getSalesDocumentsState,
    (state: State) => ({
        synced: state.synced,
    }),
);

export const getDocumentSynced = documentId => createSelector(
    getSynced,
    ({synced}) => synced.get(documentId)
);

export const getConnectionsForPositions = (positions: string[]) => createSelector(
    getDocumentsConnections,
    (connections) => flattenConnections(connections)
        .filter((c) => positions.find((p) => p === c.salesPositionId && !c.salesAttachmentId && !c.isDeleted))
);

export const getAllDeliveries = createSelector(
    getSalesDocumentsState,
    (state: State) => Object.values(state.deliveries).filter((e) => !e.isDeleted)
);

export const getDocDeliveries = createSelector(
    getAllDeliveries,
    getSelectedId,
    (deliveries, selectedId) =>
        (deliveries || []).filter((e) => e.documentId === selectedId && !e.isDeleted)
);


export const getDocumentDeliveries = documentId => createSelector(
    getAllDeliveries,
    (deliveries) => deliveries.filter((e) => e.documentId === documentId)
);

export const getSavedTagsInDb = createSelector(
    getSalesDocumentsState,
    (state: State) => state.savedTags
);
